import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Container, Card, Badge } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { login, clearError } from '../store/authSlice';
import { getApiUrl } from '../services/apiService';
import { getWebSocketUrlForDisplay } from '../hooks/useGlobalWebSocket';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [validated, setValidated] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated } = useSelector((state) => state.auth);

  useEffect(() => {
    // If already authenticated, redirect to dashboard immediately
    if (isAuthenticated) {
      console.log('LoginPage: User is authenticated, redirecting to dashboard');
      navigate('/', { replace: true });
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.log('LoginPage: User is not authenticated yet');
      }
    }

    // Clear any previous errors
    dispatch(clearError());
  }, [isAuthenticated, navigate, dispatch]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (form.checkValidity() === false) {
      event.stopPropagation();
      setValidated(true);
      return;
    }

    console.log('LoginPage: Submitting login form with username:', username);
    console.log('LoginPage: Environment:', process.env.NODE_ENV);
    console.log('LoginPage: API URL:', getApiUrl());

    try {
      // Add a timeout to prevent hanging login requests
      const loginPromise = dispatch(login({ username, password })).unwrap();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Login request timed out')), 30000)
      );

      const result = await Promise.race([loginPromise, timeoutPromise]);
      if (result && result.token) {
        console.log('LoginPage: Login successful, manually navigating to dashboard');
        navigate('/', { replace: true });
      }
    } catch (error) {
      console.error('LoginPage: Login failed:', error);
      // Ensure loading state is cleared on error
      dispatch(clearError());
    }
  };

  return (
    <div className="login-container">
      <Container>
        <div className="d-flex justify-content-center">
          <Card className="login-form">
            <Card.Body>
              <div className="login-logo">
                <h2>Leader Cloud</h2>
                <p className="text-muted">Sign in to your account</p>
                {process.env.NODE_ENV === 'development' && (
                  <div className="text-center mb-3 login-page-connection-info">
                    <small className="text-muted d-block mb-2">Development Environment</small>
                    <Badge bg="secondary" className="connection-info-badge px-2 py-1 mb-1 d-block w-100">
                      API: {getApiUrl()}
                    </Badge>
                    <Badge bg="secondary" className="connection-info-badge px-2 py-1 d-block w-100">
                      WS: {getWebSocketUrlForDisplay()}
                    </Badge>
                  </div>
                )}
              </div>

              {error && error !== 'No token found' && (
                <Alert variant="danger" onClose={() => dispatch(clearError())} dismissible>
                  {error}
                </Alert>
              )}

              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="formUsername">
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    disabled={loading}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please enter your username.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formPassword">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please enter your password.
                  </Form.Control.Feedback>
                </Form.Group>

                <div className="d-grid gap-2">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </div>
              </Form>

              <div className="mt-3 text-center">
                <small className="text-muted">
                  Need an account? <Link to="/request-account">Request access</Link>.
                </small>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </div>
  );
};

export default LoginPage;
